import React, { useMemo, useState, createElement } from 'react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Search, Filter, Clock, CheckCircle, XCircle, AlertCircle, Calendar, MessageSquare, Image as ImageIcon, FileText, Edit } from 'lucide-react';
type RequestStatus = 'pending' | 'in-progress' | 'approved' | 'rejected';
type RequestType = 'image' | 'details' | 'remove';
type Request = {
  id: string;
  optionName: string;
  category: string;
  type: RequestType;
  status: RequestStatus;
  description: string;
  submittedDate: string;
  updatedDate: string;
  adminNotes?: string;
};
const requests: Request[] = [{
  id: 'REQ-001',
  optionName: 'Black Stainless Fridge',
  category: 'Interior > Kitchen > Refrigerators',
  type: 'image',
  status: 'approved',
  description: 'Need updated product image with new model',
  submittedDate: '2024-01-20',
  updatedDate: '2024-01-22',
  adminNotes: 'Image updated successfully. New photo from manufacturer.'
}, {
  id: 'REQ-002',
  optionName: 'Gray Asphalt Shingles',
  category: 'Exterior > Roofing > Shingles',
  type: 'details',
  status: 'in-progress',
  description: 'Update warranty information from 25 to 30 years',
  submittedDate: '2024-01-21',
  updatedDate: '2024-01-21'
}, {
  id: 'REQ-003',
  optionName: 'White Vinyl Siding',
  category: 'Exterior > Siding > Vinyl',
  type: 'remove',
  status: 'pending',
  description: 'Discontinued by manufacturer, need to remove from options',
  submittedDate: '2024-01-22',
  updatedDate: '2024-01-22'
}, {
  id: 'REQ-004',
  optionName: 'Natural Oak Flooring',
  category: 'Interior > Flooring > Hardwood',
  type: 'details',
  status: 'rejected',
  description: 'Change price from $8,500 to $9,200',
  submittedDate: '2024-01-19',
  updatedDate: '2024-01-20',
  adminNotes: 'Price increase not approved. Please provide manufacturer documentation.'
}, {
  id: 'REQ-005',
  optionName: 'Built-in Fridge',
  category: 'Interior > Kitchen > Refrigerators',
  type: 'image',
  status: 'in-progress',
  description: 'Add lifestyle image showing fridge in kitchen setting',
  submittedDate: '2024-01-22',
  updatedDate: '2024-01-22'
}];
const statusConfig = {
  pending: {
    label: 'Pending',
    variant: 'warning' as const,
    icon: Clock,
    color: 'text-amber-600'
  },
  'in-progress': {
    label: 'In Progress',
    variant: 'primary' as const,
    icon: AlertCircle,
    color: 'text-blue-600'
  },
  approved: {
    label: 'Completed',
    variant: 'success' as const,
    icon: CheckCircle,
    color: 'text-green-600'
  },
  rejected: {
    label: 'Rejected',
    variant: 'danger' as const,
    icon: XCircle,
    color: 'text-red-600'
  }
};
const typeConfig = {
  image: {
    label: 'Image Update',
    icon: ImageIcon,
    color: 'text-purple-600'
  },
  details: {
    label: 'Details Update',
    icon: Edit,
    color: 'text-blue-600'
  },
  remove: {
    label: 'Remove Option',
    icon: XCircle,
    color: 'text-red-600'
  }
};
export function RequestsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | RequestStatus>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | RequestType>('all');
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
  // Filtered requests
  const filteredRequests = useMemo(() => {
    let filtered = requests;
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(r => r.status === statusFilter);
    }
    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(r => r.type === typeFilter);
    }
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => r.id.toLowerCase().includes(query) || r.optionName.toLowerCase().includes(query) || r.category.toLowerCase().includes(query) || r.description.toLowerCase().includes(query));
    }
    return filtered;
  }, [statusFilter, typeFilter, searchQuery]);
  // Stats
  const stats = useMemo(() => {
    return {
      total: requests.length,
      pending: requests.filter(r => r.status === 'pending').length,
      inProgress: requests.filter(r => r.status === 'in-progress').length,
      approved: requests.filter(r => r.status === 'approved').length,
      rejected: requests.filter(r => r.status === 'rejected').length
    };
  }, []);
  return <>
      <div className="space-y-6 max-w-7xl mx-auto" data-id="element-2133">
        <div className="flex items-center justify-between" data-id="element-2134">
          <div data-id="element-2135">
            <h1 className="text-2xl font-bold text-slate-900" data-id="element-2136">
              Change Requests
            </h1>
            <p className="text-slate-500" data-id="element-2137">
              Track your submitted option change requests
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-id="element-2138">
          <Card noPadding data-id="element-2139">
            <div className="p-4" data-id="element-2140">
              <div className="flex items-center justify-between mb-2" data-id="element-2141">
                <p className="text-sm text-slate-500" data-id="element-2142">Total Requests</p>
                <FileText className="h-4 w-4 text-slate-400" data-id="element-2143" />
              </div>
              <p className="text-2xl font-bold text-slate-900" data-id="element-2144">{stats.total}</p>
            </div>
          </Card>

          <Card noPadding data-id="element-2145">
            <div className="p-4" data-id="element-2146">
              <div className="flex items-center justify-between mb-2" data-id="element-2147">
                <p className="text-sm text-slate-500" data-id="element-2148">Pending</p>
                <Clock className="h-4 w-4 text-amber-500" data-id="element-2149" />
              </div>
              <p className="text-2xl font-bold text-amber-600" data-id="element-2150">
                {stats.pending}
              </p>
            </div>
          </Card>

          <Card noPadding data-id="element-2151">
            <div className="p-4" data-id="element-2152">
              <div className="flex items-center justify-between mb-2" data-id="element-2153">
                <p className="text-sm text-slate-500" data-id="element-2154">In Progress</p>
                <AlertCircle className="h-4 w-4 text-blue-500" data-id="element-2155" />
              </div>
              <p className="text-2xl font-bold text-blue-600" data-id="element-2156">
                {stats.inProgress}
              </p>
            </div>
          </Card>

          <Card noPadding data-id="element-2157">
            <div className="p-4" data-id="element-2158">
              <div className="flex items-center justify-between mb-2" data-id="element-2159">
                <p className="text-sm text-slate-500" data-id="element-2160">Completed</p>
                <CheckCircle className="h-4 w-4 text-green-500" data-id="element-2161" />
              </div>
              <p className="text-2xl font-bold text-green-600" data-id="element-2162">
                {stats.approved}
              </p>
            </div>
          </Card>

          <Card noPadding data-id="element-2163">
            <div className="p-4" data-id="element-2164">
              <div className="flex items-center justify-between mb-2" data-id="element-2165">
                <p className="text-sm text-slate-500" data-id="element-2166">Rejected</p>
                <XCircle className="h-4 w-4 text-red-500" data-id="element-2167" />
              </div>
              <p className="text-2xl font-bold text-red-600" data-id="element-2168">
                {stats.rejected}
              </p>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card noPadding data-id="element-2169">
          <div className="p-4 border-b border-slate-200 bg-slate-50 rounded-t-xl" data-id="element-2170">
            <div className="flex items-center gap-3" data-id="element-2171">
              {/* Search */}
              <div className="flex-1 relative" data-id="element-2172">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-2173" />
                <input type="text" placeholder="Search by ID, option name, or description..." className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2174" />
              </div>

              {/* Status Filter */}
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} className="px-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-2175">
                <option value="all" data-id="element-2176">All Status</option>
                <option value="pending" data-id="element-2177">Pending</option>
                <option value="in-progress" data-id="element-2178">In Progress</option>
                <option value="approved" data-id="element-2179">Completed</option>
                <option value="rejected" data-id="element-2180">Rejected</option>
              </select>

              {/* Type Filter */}
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value as any)} className="px-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-2181">
                <option value="all" data-id="element-2182">All Types</option>
                <option value="image" data-id="element-2183">Image Update</option>
                <option value="details" data-id="element-2184">Details Update</option>
                <option value="remove" data-id="element-2185">Remove Option</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto" data-id="element-2186">
            <table className="w-full text-left text-sm" data-id="element-2187">
              <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-2188">
                <tr data-id="element-2189">
                  <th className="px-6 py-4" data-id="element-2190">Request ID</th>
                  <th className="px-6 py-4" data-id="element-2191">Option Name</th>
                  <th className="px-6 py-4" data-id="element-2192">Category</th>
                  <th className="px-6 py-4" data-id="element-2193">Type</th>
                  <th className="px-6 py-4" data-id="element-2194">Status</th>
                  <th className="px-6 py-4" data-id="element-2195">Submitted</th>
                  <th className="px-6 py-4 text-right" data-id="element-2196">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2197">
                {filteredRequests.length === 0 ? <tr data-id="element-2198">
                    <td colSpan={7} className="px-6 py-12 text-center text-slate-500" data-id="element-2199">
                      No requests found matching your filters
                    </td>
                  </tr> : filteredRequests.map(request => {
                const StatusIcon = statusConfig[request.status].icon;
                const TypeIcon = typeConfig[request.type].icon;
                return <tr key={request.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setSelectedRequest(request)} data-id="element-2200">
                        <td className="px-6 py-4 font-medium text-slate-900" data-id="element-2201">
                          {request.id}
                        </td>
                        <td className="px-6 py-4" data-id="element-2202">
                          <div className="font-medium text-slate-900" data-id="element-2203">
                            {request.optionName}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-slate-600 text-xs" data-id="element-2204">
                          {request.category}
                        </td>
                        <td className="px-6 py-4" data-id="element-2205">
                          <div className="flex items-center gap-2" data-id="element-2206">
                            <TypeIcon className={`h-4 w-4 ${typeConfig[request.type].color}`} data-id="element-2207" />
                            <span className="text-slate-700" data-id="element-2208">
                              {typeConfig[request.type].label}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-2209">
                          <Badge variant={statusConfig[request.status].variant} data-id="element-2210">
                            <div className="flex items-center gap-1" data-id="element-2211">
                              <StatusIcon className="h-3 w-3" data-id="element-2212" />
                              {statusConfig[request.status].label}
                            </div>
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-slate-600" data-id="element-2213">
                          <div className="flex items-center gap-1" data-id="element-2214">
                            <Calendar className="h-3 w-3 text-slate-400" data-id="element-2215" />
                            {new Date(request.submittedDate).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-2216">
                          <Button variant="ghost" size="sm" data-id="element-2217">
                            View Details
                          </Button>
                        </td>
                      </tr>;
              })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Request Details Modal */}
      {selectedRequest && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-2218">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" data-id="element-2219">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white" data-id="element-2220">
              <div data-id="element-2221">
                <h2 className="text-xl font-bold text-slate-900" data-id="element-2222">
                  Request Details
                </h2>
                <p className="text-sm text-slate-500 mt-1" data-id="element-2223">
                  {selectedRequest.id}
                </p>
              </div>
              <button onClick={() => setSelectedRequest(null)} className="text-slate-400 hover:text-slate-600 transition-colors" data-id="element-2224">
                <XCircle className="h-5 w-5" data-id="element-2225" />
              </button>
            </div>

            <div className="p-6 space-y-6" data-id="element-2226">
              {/* Status & Type */}
              <div className="flex items-center gap-3" data-id="element-2227">
                <Badge variant={statusConfig[selectedRequest.status].variant} className="text-sm" data-id="element-2228">
                  <div className="flex items-center gap-1" data-id="element-2229">
                    {createElement(statusConfig[selectedRequest.status].icon, {
                  className: 'h-3 w-3'
                })}
                    {statusConfig[selectedRequest.status].label}
                  </div>
                </Badge>
                <Badge variant="neutral" className="text-sm" data-id="element-2230">
                  <div className="flex items-center gap-1" data-id="element-2231">
                    {createElement(typeConfig[selectedRequest.type].icon, {
                  className: 'h-3 w-3'
                })}
                    {typeConfig[selectedRequest.type].label}
                  </div>
                </Badge>
              </div>

              {/* Option Info */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-3" data-id="element-2232">
                <div data-id="element-2233">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-2234">Option Name</p>
                  <p className="font-medium text-slate-900" data-id="element-2235">
                    {selectedRequest.optionName}
                  </p>
                </div>
                <div data-id="element-2236">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-2237">Category Path</p>
                  <p className="text-sm text-slate-700" data-id="element-2238">
                    {selectedRequest.category}
                  </p>
                </div>
              </div>

              {/* Description */}
              <div data-id="element-2239">
                <p className="text-sm font-medium text-slate-700 mb-2" data-id="element-2240">
                  Description
                </p>
                <div className="bg-slate-50 rounded-lg p-4" data-id="element-2241">
                  <p className="text-slate-700" data-id="element-2242">
                    {selectedRequest.description}
                  </p>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4" data-id="element-2243">
                <div data-id="element-2244">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-2245">Submitted Date</p>
                  <div className="flex items-center gap-2 text-slate-700" data-id="element-2246">
                    <Calendar className="h-4 w-4 text-slate-400" data-id="element-2247" />
                    {new Date(selectedRequest.submittedDate).toLocaleDateString()}
                  </div>
                </div>
                <div data-id="element-2248">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-2249">Last Updated</p>
                  <div className="flex items-center gap-2 text-slate-700" data-id="element-2250">
                    <Clock className="h-4 w-4 text-slate-400" data-id="element-2251" />
                    {new Date(selectedRequest.updatedDate).toLocaleDateString()}
                  </div>
                </div>
              </div>

              {/* Admin Notes */}
              {selectedRequest.adminNotes && <div data-id="element-2252">
                  <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2" data-id="element-2253">
                    <MessageSquare className="h-4 w-4" data-id="element-2254" />
                    Admin Notes
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-2255">
                    <p className="text-sm text-blue-900" data-id="element-2256">
                      {selectedRequest.adminNotes}
                    </p>
                  </div>
                </div>}
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end" data-id="element-2257">
              <Button onClick={() => setSelectedRequest(null)} data-id="element-2258">Close</Button>
            </div>
          </div>
        </div>}
    </>;
}